import { useSelector } from "react-redux";

import { Stack } from "@similarweb/ui-components/foundations";

import MessageLoader from "app/pages/sales-intelligence/modules/ai-agent/components/MessageLoader";
import MessagesList from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/MessagesList";
import type { ChatUiContext } from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/types";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import { selectChatMessages } from "app/pages/sales-intelligence/modules/ai-agent/store";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { AIAgentEmailSuggestions } from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentEmailSuggestions/AIAgentEmailSuggestions";
import { AIAgentEmailVisuals } from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentEmailVisuals/AIAgentEmailVisuals";
import { EmailCompetitors } from "app/pages/sales-intelligence/pages/ai-agent/containers/EmailCompetitors/EmailCompetitors";

import { AIAgentEmailRefinePanelLoader } from "./AIAgentEmailRefinePanelLoader";

type AIAgentEmailRefineProps = {
    isLoading: boolean;
    isLoadingMessages: boolean;
    isUpdatingVersion: boolean;
    isSuggestionsVisible: boolean;
    isCompetitorsShown: boolean;
    emailPreviews?: ParsedAIEmailPreview[];
    selectedCompany: AiAgentEmailAutocompleteCompany | null;
    competitors: Competitor[];
    onCompetitorsChange: (competitors: Competitor[]) => void;
    lastUserMessageRef: React.RefObject<HTMLDivElement | null>;
    lastAiMessageRef: React.RefObject<HTMLDivElement | null>;
    isPreviewsShown: boolean;
    onApplyVisual: (visual: ParsedAIEmailPreview) => void;
    onPresentVisuals: () => void;
    onApplyCompetitorsSuggestion: () => void;
    onVersionClick: (id: string) => void;
};

const UI_CONTEXT: ChatUiContext = {
    chatTheme: "embedded",
    renderMode: "flat",
    loader: <MessageLoader variant="version" />,
};

export const AIAgentEmailRefinePanelContent = ({
    isLoading,
    isLoadingMessages,
    isUpdatingVersion,
    isSuggestionsVisible,
    isCompetitorsShown,
    emailPreviews,
    selectedCompany,
    competitors,
    onCompetitorsChange,
    lastUserMessageRef,
    lastAiMessageRef,
    isPreviewsShown,
    onApplyVisual,
    onPresentVisuals,
    onApplyCompetitorsSuggestion,
    onVersionClick,
}: AIAgentEmailRefineProps) => {
    const messages = useSelector(selectChatMessages);

    if (isUpdatingVersion || isLoading) {
        return (
            <AIAgentEmailRefinePanelLoader
                isUpdatingVersion={isUpdatingVersion}
                isLoading={isLoading}
            />
        );
    }
    return (
        <Stack>
            <MessagesList
                uiContext={UI_CONTEXT}
                isLoading={isLoadingMessages}
                messages={messages}
                lastUserMessageRef={lastUserMessageRef}
                lastAiMessageRef={lastAiMessageRef}
                withControls={false}
                onVersionMessageClick={onVersionClick}
            />
            {isPreviewsShown ? (
                <AIAgentEmailVisuals
                    emailPreviews={emailPreviews}
                    selectedCompany={selectedCompany}
                    onApplyVisual={onApplyVisual}
                />
            ) : null}
            {isCompetitorsShown ? (
                <EmailCompetitors
                    value={competitors}
                    onChange={onCompetitorsChange}
                />
            ) : null}
            <AIAgentEmailSuggestions
                isSuggestionsVisible={isSuggestionsVisible}
                isVisualSuggestionVisible={!isPreviewsShown}
                isCompetitorsSuggestionVisible={!isCompetitorsShown}
                onPresentVisuals={onPresentVisuals}
                onPresentCompetitors={onApplyCompetitorsSuggestion}
            />
        </Stack>
    );
};
